import { ComponentType, useEffect, useState } from 'react';
import loadScript from 'load-script2';
import { loadCSS } from 'fg-loadcss';
import Documents from './Documents';
import { logDebugLevel } from './GlobalFunctions';
import { computeBookingUrl } from './components/CTASegment';

let calendlyLoadState = 'unloaded';

declare global {
  interface Window {
    Calendly: { showPopupWidget: (arg0: string) => void };
  }
}

type CalendlyHook = (onShowError?: (arg0: string) => void) => void;

const useCalendly = (newBookingUrl?: string): CalendlyHook => {
  const [myState, setMyState] = useState({
    loadCalendlyAttempts: 0,
  });

  const [bookingUrl, setBookingUrl] = useState<string>(Documents.bookDemo);

  useEffect(() => {
    if (calendlyLoadState === 'unloaded') {
      calendlyLoadState = 'loading';
      loadScript('https://calendly.com/assets/external/widget.js')
        .then(() => loadCSS('https://calendly.com/assets/external/widget.css'))
        .then(() => (calendlyLoadState = 'loaded'))
        .catch((error: unknown) => {
          // Calendly is third-party and blockable - by an ad blocker, a school
          // network, or simply being offline. Without this the rejection is
          // unhandled, and calendlyLoadState stays 'loading' forever so the
          // retry below can never re-attempt the load.
          console.warn('Could not load the Calendly widget:', error);
          calendlyLoadState = 'unloaded';
        });
    }
  }, [myState, setMyState]);

  useEffect(() => {
    if (newBookingUrl) {
      logDebugLevel('useCalendly', 'newBookingUrl', newBookingUrl);
      setBookingUrl(newBookingUrl);
    } else {
      setBookingUrl(computeBookingUrl());
    }
  }, [newBookingUrl]);

  return openCalendly;

  function openCalendly(onShowError?: (arg0: string) => void) {
    if (calendlyLoadState === 'loaded') {
      window.Calendly.showPopupWidget(bookingUrl);
    } else if (myState.loadCalendlyAttempts > 2) {
      setMyState({ loadCalendlyAttempts: 0 });
      calendlyLoadState = 'unloaded';
      if (onShowError) {
        onShowError('Sorry, the booking calendar isn’t available at the moment. Please try again later.');
      }
    } else {
      setTimeout(() => {
        setMyState({
          loadCalendlyAttempts: (myState.loadCalendlyAttempts += 1),
        });
        openCalendly(onShowError);
      }, 1000);
    }
  }
};

type CalendlyFunctionType = ReturnType<typeof useCalendly>;

export function withCalendly<T>(WrappedComponent: ComponentType<T>): ComponentType<T & CalendlyFunctionType> {
  // Uppercase: this IS a React component, and react-hooks/rules-of-hooks only
  // recognises it as one - and so only permits the useCalendly call - when the
  // name says so.
  const WithCalendly = (props: T) => {
    const onOpenCalendly = useCalendly();
    return <WrappedComponent {...props} onOpenCalendly={onOpenCalendly} />;
  };
  WithCalendly.displayName = `withCalendly(${WrappedComponent.displayName || WrappedComponent.name})`;
  return WithCalendly;
}

export default useCalendly;
